import React, { useState, useEffect } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Typography,
    TextField as MuiTextField, OutlinedInput, LinearProgress, Checkbox, Chip
} from "@mui/material";
import useStyles from "../../../assets/styles/AssessmentPageStyles";
import { PROGRAM_TARGETS } from "../../../utils/META/cat_108";
import {Field} from "formik";
import {FormControl, InputLabel, ListItemText, MenuItem, Select, Tab, Tabs, TextField} from "@material-ui/core";
import {useHistory, useLocation} from "react-router-dom";
import {
    buildInsiderAssessmentService,
    getInsiderAssessmentDescriptionService
} from "../../../services/assessments.service";
import {uploadMedia} from "../../../services/public.service";
import { SketchPicker } from 'react-color';
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import {LANGUAGES_SUPPORTED} from "../../../utils/constants";
import {
    buildLearningProgramService,
    getLearningProgramDescriptionService
} from "../../../services/learning_programs.service";
import {toastWarning} from "../../../utils/utils";
import { Restore } from '@material-ui/icons';
import { IconButton } from '@mui/material';
import { SUBJECTS } from "../../../utils/dq_program_constants";

const parseInitialValues = (valueString, options) => {
    if (!valueString) return [];
    const valuesArray = valueString.split(',').map(val => val.trim());
    return options.filter(opt => valuesArray.includes(opt.value));
};

const FormTextField = ({ label, name, required = true, multiline = false, maxRows, helperText, error, validate, ...rest }) => {
    const [isFocused, setIsFocused] = useState(false);
    const classes = useStyles();

    return (
        <Field name={name} validate={validate}>
            {({ field, form }) => (
                <FormControl variant="outlined" required className={classes.textFieldRoot} fullWidth style={{marginTop:'0px !important'}}>
                    <InputLabel htmlFor={name} shrink={isFocused || field.value}>
                        {label}
                    </InputLabel>
                    <OutlinedInput
                        {...field}
                        id={name}
                        label={label}
                        fullWidth
                        required={required}
                        multiline={multiline}
                        rowsMax={maxRows}
                        onFocus={() => setIsFocused(true)}
                        onBlur={(e) => {
                            setIsFocused(false);
                            field.onBlur(e);
                        }}
                        error={error}
                        aria-describedby={`${name}-helper-text`}
                        {...rest}
                    />
                    {helperText && (
                        <div id={`${name}-helper-text`} style={{ color: error ? 'red' : 'inherit' }}>
                            {helperText}
                        </div>
                    )}
                </FormControl>
            )}
        </Field>
    );
};

const DQProProgramName = () => {
    const classes = useStyles();
    const history   = useHistory();
    const { state } = useLocation();
    const buildData = state?.buildData || {};
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const programDetails = location.state;
    // Program content language from program creation (user already selected in wizard)
    const programContentLanguage = programDetails?.ContentLanguages?.[0];
    const isEnglishOnly = !programContentLanguage || programContentLanguage === 'en-US';
    const selectedLanguages = !isEnglishOnly ? [programContentLanguage] : [];
    // Other language first when multiple languages
    const nameTabLanguages = isEnglishOnly ? ['en-US'] : [programContentLanguage, 'en-US'];

    // Form state
    const [step, setStep] = useState(1); // 1-4 steps
    const [programName, setProgramName] = useState('');
    const [programDescription, setProgramDescription] = useState( '');
    const [programTarget, setProgramTarget] = useState([]);
    const [programTargetOther, setProgramTargetOther] = useState('');

    const targetAudienceOptions = Object.values(PROGRAM_TARGETS);

    // Default colors
    const DEFAULT_THEME_COLOR = "#4A90E2";
    const DEFAULT_BADGE_COLOR = "#1A194C";
    const DEFAULT_PRIMARY_COLOR = "#FFFFFF";

    const [themeColor, setThemeColor] = useState(buildData.theme_color || DEFAULT_THEME_COLOR);
    const [badgeColor, setBadgeColor] = useState(buildData.badge_background || DEFAULT_BADGE_COLOR);
    const [primaryColor, setPrimaryColor] = useState(buildData.primary_color || DEFAULT_PRIMARY_COLOR);

    // Preset colors for each picker
    const themeColorPresets = [
        "#4A90E2", "#FF6468", "#ED6628", "#0097B2", "#8C74A7", "#D44C71", "#C91C6E", "#6A8EC9"
    ];
    const badgeColorPresets = [
        "#1A194C", "#000000", "#2C3E50", "#34495E", "#1A237E", "#0D47A1", "#1B5E20", "#4A148C"
    ];
    const primaryColorPresets = [
        "#FFFFFF", "#000000", "#F5F5F5", "#E0E0E0", "#FFFAF0", "#FFF8DC", "#F0F8FF", "#F5FFFA"
    ];

    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadedFileUrl, setUploadedFileUrl] = useState('');

    const [nameTranslations, setNameTranslations] = useState({});
    const [descTranslations, setDescTranslations] = useState({});

    const [currentLanguageTab, setCurrentLanguageTab] = useState(() => {
        const lang = programDetails?.ContentLanguages?.[0];
        return lang && lang !== 'en-US' ? lang : 'en-US';
    });
    const [translations, setTranslations] = useState({});

    const handleNameTranslationChange = (lang, value) => {
        setNameTranslations(prev => ({
            ...prev,
            [lang]: value
        }));
    };
    const handleDescTranslationChange = (lang, value) => {
        setDescTranslations(prev => ({
            ...prev,
            [lang]: value
        }));
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    const handleAutocompleteChange = (event, value) => {
        setProgramTarget(value);
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setFile(file);
            setUploading(true);
            setUploadProgress(0);

            const formData = new FormData();
            formData.append('media', file);

            try {
                const result = await uploadMedia(formData, (progressEvent) => {
                    if (progressEvent.total) {
                        const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                        setUploadProgress(percent);
                    }
                });
                if (result?.data?.media_path) {
                    setUploadedFileUrl(result.data.media_path);
                }
            } catch (error) {
                alert("Error uploading file");
            } finally {
                setUploading(false);
            }
        }
    };

    const buildTranslationsPayload = () => {
        const translationsPayload = {};
        selectedLanguages.forEach(lang => {
            translationsPayload[lang] = {
                name: nameTranslations[lang] || "",
                description: descTranslations[lang] || ""
            };
        });
        return translationsPayload;
    };

    const handleSubmit = async () => {
        if (!programDetails) {
            toastWarning("Missing base program information!");
            return;
        }
        console.log('programDetails is', programDetails);
        
        const translationsPayload = buildTranslationsPayload();
        console.log("Program Name:", programName);
        const payload = {
            code: programDetails.CourseCode, // Generates a unique UUID,
            full_name: programName,
            description: programDescription,
            target_audience: programDetails.Age,
            dq_codes: programDetails.Competencies,
            time_to_complete_minutes: programDetails.SuggestApproxHours,
            image_url: uploadedFileUrl,
            theme_color: themeColor,
            badge_background: badgeColor,
            primary_color: primaryColor,
            translations: translationsPayload,
            program_details: programDetails,
        };
        payload.program_details.CourseCode = payload.code;
        const request_type = 'new';

        try {
            const program = await buildLearningProgramService(
                payload.code,
                payload.full_name,
                payload.target_audience,
                payload.dq_codes,
                payload.time_to_complete_minutes,
                payload.image_url,
                payload.theme_color,
                payload.badge_background,
                payload.primary_color,
                payload.description,
                payload.more_info,
                payload.translations,
                payload.program_details,
                request_type
            );
            console.log("Program:", program);
            history.push({
                pathname: `/dq_pro/products/program/dashboard/${program.data.code}`,
                state: program
            });
        } catch (err) {
            console.error(err);
        }
    };

    const nextStep = async () => {
        if (step === 1) {
            setLoading(true);
            try {
                const target_audience = programDetails.Age;
                // Send language-only locales (e.g. 'en', 'es') - no country. English always included; add second language from wizard if non-English.
                const secondLang = programContentLanguage && programContentLanguage !== 'en-US'? programContentLanguage.split('-')[0]: null;
                const locales = secondLang ? ['en', secondLang] : ['en'];
                const dq_codes = programDetails.Competencies;
                // Convert SubjectArea integer to label using SUBJECTS constant
                const subject_area = programDetails.SubjectArea
                    ? SUBJECTS.find(s => s.value === programDetails.SubjectArea)?.label
                    : null;
                if (target_audience && dq_codes.length > 0) {
                    const response = await getLearningProgramDescriptionService(
                            target_audience,
                            locales,
                            dq_codes,
                            subject_area
                    );
                    if (response?.data?.descriptions) {
                        // API returns descriptions keyed by language-only locale (en, es). Map to full locale keys (en-US, es-ES) for state.
                        const raw = response.data.descriptions;
                        const normalized = {};
                        if (raw.en) {
                            normalized['en-US'] = raw.en;
                            setProgramDescription(raw.en);
                        }
                        if (secondLang && raw[secondLang] !== undefined) {
                            normalized[programContentLanguage] = raw[secondLang];
                        }
                        setDescTranslations(normalized);
                    }
                }
            } catch (err) {
                // Optionally handle error
            }
            setLoading(false);
        }
        setStep(prev => prev + 1);
    }

    const prevStep = () => setStep(prev => prev - 1);

    return (
        <>
        <LoaderWithBackDrop loading={loading} />
            <Box className={classes.pageContainer} sx={{ pt: 4, mt: 'auto' }}>
                {/* Step 1: Name Your Program */}
                {step === 1 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', maxWidth: 800, mx: 'auto', px: 2, mt: 'auto' }}>
                        <Typography variant="h3" className={classes.title} sx={{ mb: 6, textAlign: 'center' }}>
                            Let's Name Your Program
                        </Typography>
                        <Typography sx={{ mb: 4, fontSize: '21px' }}>
                            This is the name of the program that your learners will see.<br />
                            (Max 60 characters)
                        </Typography>
                        {isEnglishOnly ? (
                            <FormControl fullWidth variant="outlined" className={classes.inputField}
                                         style={{ width: "80%", marginBottom: '20px', borderRadius: '5px' }}>
                                <TextField
                                    label="Program Name"
                                    variant="outlined"
                                    required
                                    value={programName}
                                    onChange={(e) => setProgramName(e.target.value)}
                                    inputProps={{ maxLength: 60 }}
                                    InputLabelProps={{
                                        style: { color: '#929EA7' },
                                        sx: {
                                            '&.Mui-focused': {
                                                color: '#929EA7',
                                            },
                                        }
                                    }}
                                />
                            </FormControl>
                        ) : (
                            <Box sx={{ width: '100%', mt: 2, borderRadius: '5px', border: '2px solid #ff6468ff' }}>
                                <Tabs
                                    value={currentLanguageTab}
                                    onChange={(e, newValue) => setCurrentLanguageTab(newValue)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    TabIndicatorProps={{ style: { display: 'none' } }}
                                    style={{
                                        mb: 2,
                                        backgroundColor: '#ffffff'
                                    }}
                                >
                                    {nameTabLanguages.map((lang) => (
                                        <Tab
                                            key={lang}
                                            label={LANGUAGES_SUPPORTED[lang]}
                                            value={lang}
                                            style={{
                                                textTransform: 'none',
                                                minWidth: 100,
                                                padding: '8px 16px',
                                                marginRight: 1,
                                                borderBottom: currentLanguageTab === lang ? '4px solid #ED6628' : '4px solid transparent',
                                                color: currentLanguageTab === lang ? '#ED6628' : '#666',
                                                fontWeight: currentLanguageTab === lang ? 'bold' : 'normal',
                                                backgroundColor: currentLanguageTab === lang ? '#FFE2D6' : 'transparent',
                                                borderTopLeftRadius: '6px',
                                                borderTopRightRadius: '6px',
                                            }}
                                        />
                                    ))}
                                </Tabs>

                                <Box sx={{ pt: 3, px: 2, pb: 2 }}>
                                    {nameTabLanguages.map((lang) => (
                                        <div
                                            key={lang}
                                            role="tabpanel"
                                            hidden={currentLanguageTab !== lang}
                                        >
                                            <FormControl fullWidth variant="outlined" className={classes.inputField} style={{ width: "100%", marginBottom: '12px', borderRadius: '5px' }}>
                                                <TextField
                                                    variant="outlined"
                                                    label={lang === 'en-US' ? 'Program Name (English)' : `Program Name in ${LANGUAGES_SUPPORTED[lang]}`}
                                                    value={lang === 'en-US' ? programName : (nameTranslations[lang] || '')}
                                                    onChange={(e) => lang === 'en-US' ? setProgramName(e.target.value) : handleNameTranslationChange(lang, e.target.value)}
                                                    required
                                                    error={lang === 'en-US' ? !programName.trim() : !nameTranslations[lang]?.trim()}
                                                    helperText={lang === 'en-US' ? (!programName.trim() ? 'Program name is required' : '') : (!nameTranslations[lang]?.trim() ? 'Program name is required' : '')}
                                                    inputProps={{ maxLength: 60 }}
                                                    InputLabelProps={{
                                                        style: { color: '#929EA7' },
                                                        sx: {
                                                            '&.Mui-focused': {
                                                                color: '#929EA7',
                                                            },
                                                        }
                                                    }}
                                                />
                                            </FormControl>
                                        </div>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Step 2: Program Description */}
                {step === 2 && (
                    <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto', px: 2, mt:'auto' }}>
                        <Typography variant="h3" className={classes.title} sx={{ mb: 6, textAlign: 'center' }}>
                            Program Description
                        </Typography>
                        <Typography variant="h5" sx={{ mb: 4 }}>
                            Here, we’ve written an program description that will appear to your learners, explaining what the program is about. You can edit it by typing in the box below.
                        </Typography>
                        <Box sx={{ width: '80%', mx: 'auto', borderRadius: '5px', border: '2px solid #ff6468ff' }}>
                            {isEnglishOnly ? (
                                <Box sx={{ p: 2 }}>
                                    <FormControl fullWidth variant="outlined" className={classes.languageInputField}
                                                 style={{ marginBottom: 0, borderRadius: '5px' }}>
                                        <TextField
                                            variant="outlined"
                                            multiline
                                            rows={6}
                                            value={programDescription}
                                            onChange={(e)=>setProgramDescription(e.target.value)}
                                            label="Short description"
                                            InputLabelProps={{
                                                style: { color: '#929EA7' },
                                                sx: {
                                                    '&.Mui-focused': {
                                                        color: '#929EA7',
                                                    },
                                                }
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                            ) : (
                                <>
                                    <Tabs
                                        value={currentLanguageTab}
                                        onChange={(e, newValue) => setCurrentLanguageTab(newValue)}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        TabIndicatorProps={{ style: { display: 'none' } }}
                                        style={{
                                            mb: 2,
                                            backgroundColor: '#ffffff'
                                        }}
                                    >
                                        {nameTabLanguages.map((lang) => (
                                            <Tab
                                                key={lang}
                                                label={LANGUAGES_SUPPORTED[lang]}
                                                value={lang}
                                                style={{
                                                    textTransform: 'none',
                                                    minWidth: 100,
                                                    padding: '8px 16px',
                                                    marginRight: 1,
                                                    borderBottom: currentLanguageTab === lang ? '4px solid #ED6628' : '4px solid transparent',
                                                    color: currentLanguageTab === lang ? '#ED6628' : '#666',
                                                    fontWeight: currentLanguageTab === lang ? 'bold' : 'normal',
                                                    backgroundColor: currentLanguageTab === lang ? '#FFE2D6' : 'transparent',
                                                    borderTopLeftRadius: '6px',
                                                    borderTopRightRadius: '6px',
                                                }}
                                            />
                                        ))}
                                    </Tabs>

                                    <Box sx={{ pt: 2, px: 2, pb: 2 }}>
                                        {nameTabLanguages.map((lang) => (
                                            <div
                                                key={lang}
                                                role="tabpanel"
                                                hidden={currentLanguageTab !== lang}
                                            >
                                                <FormControl fullWidth variant="outlined" className={classes.languageInputField}
                                                             style={{ marginBottom: 0, borderRadius: '5px' }}>
                                                    <TextField
                                                        variant="outlined"
                                                        multiline
                                                        rows={6}
                                                        value={lang === 'en-US' ? programDescription : (descTranslations[lang] || '')}
                                                        onChange={(e) => lang === 'en-US' ? setProgramDescription(e.target.value) : handleDescTranslationChange(lang, e.target.value)}
                                                        label={lang === 'en-US' ? 'Short description (English)' : `Short description in ${LANGUAGES_SUPPORTED[lang]}`}
                                                        InputLabelProps={{
                                                            style: { color: '#929EA7' },
                                                            sx: {
                                                                '&.Mui-focused': {
                                                                    color: '#929EA7',
                                                                },
                                                            }
                                                        }}
                                                    />
                                                </FormControl>
                                            </div>
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Step 3: Upload Program Icon */}
                {step === 3 && (
                    <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto', px: 2 }}>
                        <Typography variant="h3" className={classes.title} sx={{ mb: 6, textAlign: 'center' }}>
                            Upload Program Icon
                        </Typography>
                        <Typography variant="h5" sx={{ mb: 4 }}>
                            You can upload an image to use as your program icon.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection:'column', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
                            <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="program-image-upload"
                                onChange={handleFileChange}
                            />
                            <label htmlFor="program-image-upload">
                                <Button variant="outlined" component="span"
                                        sx={{
                                            backgroundColor: '#ff6468ff',
                                            color: '#ffffff',
                                            width: '100%',
                                            fontWeight: 'bold',
                                            fontSize: '16px',
                                            mt: 6,
                                            padding: '10px 50px',
                                            borderRadius: '20px',
                                            '&:hover': {
                                                backgroundColor: '#ff6468ff',
                                                color: '#ffffff'
                                            }
                                        }}>
                                    Choose Icon
                                </Button>
                            </label>
                            {file && <Typography sx={{ mr: 2 }}>{file.name}</Typography>}
                        </Box>
                        {uploading && (
                            <Box sx={{ width: '60%', mx: 'auto', mb: 2 }}>
                                <LinearProgress variant="determinate" value={uploadProgress} />
                            </Box>
                        )}
                        {uploadedFileUrl && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                <Typography color="success.main">Icon uploaded!</Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Step 4: Color Theme */}
                {step === 4 && (
                    <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto', px: 2 }}>
                        <Typography variant="h3" className={classes.title} sx={{ mb: 6, textAlign: 'center' }}>
                            Color Theme
                        </Typography>
                        <Typography variant="h5" sx={{ mb: 4 }}>
                            Select your program color theme.
                        </Typography>

                        {/* Preview Strip */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                borderRadius: 0,
                                height: '80px',
                                overflow: 'hidden',
                                mb: 5,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                position: 'relative',
                                width: '80%',
                                mx: 'auto',
                            }}
                        >
                            {/* Reset Icon Button - Top Right */}
                            <IconButton
                                onClick={() => {
                                    setThemeColor(DEFAULT_THEME_COLOR);
                                    setBadgeColor(DEFAULT_BADGE_COLOR);
                                    setPrimaryColor(DEFAULT_PRIMARY_COLOR);
                                }}
                                sx={{
                                    position: 'absolute',
                                    top: 7,
                                    right: 7,
                                    zIndex: 10,
                                    backgroundColor: 'transparent',
                                    color: '#ffff',
                                    width: 24,
                                    height: 24,
                                    padding: 0,
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        transform: 'scale(1.1)',
                                    },
                                }}
                            >
                                <Restore sx={{ fontSize: 18 }} />
                            </IconButton>

                            {/* Left Vertical Bar - Theme Color */}
                            <Box
                                sx={{
                                    width: '13px',
                                    height: '100%',
                                    backgroundColor: themeColor,
                                    flexShrink: 0,
                                }}
                            />

                            {/* Full Background with Icon and Title */}
                            <Box
                                sx={{
                                    flex: 1,
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    px: 3,
                                    backgroundColor: badgeColor,
                                }}
                            >
                                {/* Icon */}
                                <img
                                    src={uploadedFileUrl || "https://cdn-icons-png.flaticon.com/512/1828/1828884.png"}
                                    alt="Program Icon"
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        objectFit: 'contain',
                                        filter: uploadedFileUrl ? 'none' : 'brightness(0) invert(1)',
                                    }}
                                />

                                {/* Title - prefer other language name when user entered one */}
                                <Typography 
                                    sx={{ 
                                        color: primaryColor, 
                                        fontWeight: 'bold',
                                        fontSize: '22px',
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    {(!isEnglishOnly && nameTranslations[programContentLanguage]?.trim()) 
                                        ? nameTranslations[programContentLanguage] 
                                        : (programName || "Program Title")}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Color Pickers */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 4,
                                flexWrap: 'wrap',
                                my: 4,
                            }}
                        >
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    Theme Color
                                </Typography>
                                <SketchPicker
                                    color={themeColor}
                                    onChangeComplete={(color) => setThemeColor(color.hex)}
                                    presetColors={themeColorPresets}
                                    width={180}
                                />
                            </Box>

                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    Badge Background
                                </Typography>
                                <SketchPicker
                                    color={badgeColor}
                                    onChangeComplete={(color) => setBadgeColor(color.hex)}
                                    presetColors={badgeColorPresets}
                                    width={180}
                                />
                            </Box>

                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    Primary Color
                                </Typography>
                                <SketchPicker
                                    color={primaryColor}
                                    onChangeComplete={(color) => setPrimaryColor(color.hex)}
                                    presetColors={primaryColorPresets}
                                    width={180}
                                />
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Navigation Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 2 }}>
                    {step > 1 && (
                        <Button
                            onClick={prevStep}
                            variant="outlined"
                            sx={{
                                borderColor: '#0097b2',
                                color: '#0097b2',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                padding: '10px 30px',
                                borderRadius: '20px',
                                minWidth: '150px',
                                '&:hover': {
                                    borderColor: '#0097b2',
                                    backgroundColor: 'rgba(0, 151, 178, 0.04)'
                                }
                            }}
                        >
                            Back
                        </Button>
                    )}

                    {step < 4 ? (
                        <Button
                            onClick={nextStep}
                            variant="contained"
                            disabled={!programName.trim() || selectedLanguages.some(lang => !nameTranslations[lang]?.trim())}
                            sx={{
                                backgroundColor: '#ff6468ff',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                padding: '10px 30px',
                                borderRadius: '20px',
                                minWidth: '150px',
                                '&:hover': {
                                    backgroundColor: '#ff6468ff',
                                    opacity: 0.9
                                },
                                '&:disabled': {
                                    backgroundColor: '#cccccc',
                                    color: '#666666'
                                }
                            }}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: '#ff6468ff',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                padding: '10px 30px',
                                borderRadius: '20px',
                                minWidth: '150px',
                                '&:hover': {
                                    backgroundColor: '#ff6468ff',
                                    opacity: 0.9
                                },
                                '&:disabled': {
                                    backgroundColor: '#cccccc',
                                    color: '#666666'
                                }
                            }}
                            onClick={handleSubmit}
                            disabled={!programName.trim() || selectedLanguages.some(lang => !nameTranslations[lang]?.trim())}
                        >
                            Submit
                        </Button>
                    )}
                </Box>
            </Box>
        </>
    );
};

export default DQProProgramName;
